import React, { useEffect, useMemo, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useOrderSave } from "../context/OrderSaveContext";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";
import { useStaticOptions } from "../context/StaticOptionsContext";

const deriveColorMode = (order) => {
    const leatherColor = order.hmeliorder?.hmeliorder_leather_color?.toString().trim() || "";

    if (!leatherColor) {
        return "";
    }

    return leatherColor.includes(",") ? "Többszínű" : "Egyszínű";
};

const EgySzinu = () => {
    const navigate = useStepNavigate();
    const { order, updateOrder } = useOrder();
    const { saveOrder } = useOrderSave();
    const { staticOptions } = useStaticOptions();

    const shoeColorOptionsRaw = staticOptions?.shoe_color_options || [];

    const shoe_colors = useMemo(
        () =>
            (shoeColorOptionsRaw || [])
                .filter((color) => Number(color.cselicolor_color_1) === 1)
                .map((c, idx) => {
                    const label = [
                        c.cselicolor_color_national,
                        c.cselicolor_top_leather_name,
                    ]
                        .filter(Boolean)
                        .join(" / ");

                    return {
                        id: `${c.cselicolor_szam ?? "x"}_${c.cselicolor_top_leather_szam ?? "y"}_${idx}`,
                        color_text: label,
                        baseId: Number(c.cselicolor_szam) || null,
                    };
                }),
        [shoeColorOptionsRaw]
    );

    const initialLeatherColor = order.hmeliorder?.hmeliorder_leather_color?.toString() || "";
    const initialColorId =
        shoe_colors.find((color) => color.color_text === initialLeatherColor)?.id || "";

    const [selectedMode, setSelectedMode] = useState(() => deriveColorMode(order));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedColorId, setSelectedColorId] = useState(initialColorId);
    const [selectedColorName, setSelectedColorName] = useState(initialLeatherColor);

    const showOptions = selectedMode === "Egyszínű";

    useEffect(() => {
        if (shoeColorOptionsRaw.length > 0) {
            setLoading(false);
        }
    }, [shoeColorOptionsRaw.length]);

    useEffect(() => {
        const currentColorName = order.hmeliorder?.hmeliorder_leather_color?.toString() || "";
        const matchedColorId =
            shoe_colors.find((color) => color.color_text === currentColorName)?.id || "";

        setSelectedMode(deriveColorMode(order));
        setSelectedColorId(matchedColorId);
        setSelectedColorName(currentColorName);
    }, [order.hmeliorder?.hmeliorder_leather_color, shoe_colors]);

    const handleAnswer = (answer) => {
        setSelectedMode(answer);

        if (answer === "Egyszínű") {
            setSelectedColorId("");
            setSelectedColorName("");

            updateOrder({
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_leather_color: "",
                    hmeliorder_color_1: 1,
                    hmeliorder_color_2: 1,
                    hmeliorder_color_3: 1,
                },
            });
            return;
        }

        setSelectedColorId("");
        setSelectedColorName("");
        updateOrder({
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_leather_color: "",
                hmeliorder_color_1: 1,
                hmeliorder_color_2: 1,
                hmeliorder_color_3: 1,
            },
        });
    };

    const handleColorSelect = (color) => {
        setSelectedColorId(color.id);
        setSelectedColorName(color.color_text);
        updateOrder({
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_leather_color: color.color_text,
                hmeliorder_color_1: color.baseId,
                hmeliorder_color_2: 1,
                hmeliorder_color_3: 1,
            },
        });
    };

    const handleBackClick = () => {
        navigate("/SpecialisIgeny");
    };

    const handleNext = async () => {
        if (selectedMode === "Többszínű") {
            navigate("/TobbSzinu");
            return;
        }

        if (selectedMode === "Egyszínű") {
            const payload = {
                ...order,
                hmeliorder: {
                    ...order.hmeliorder,
                    hmeliorder_leather_color: selectedColorName,
                    hmeliorder_color_1: shoe_colors.find((c) => c.id === selectedColorId)?.baseId ?? null,
                    hmeliorder_color_2: 1,
                    hmeliorder_color_3: 1,
                },
            };

            updateOrder(payload);
            const result = await saveOrder(payload);

            if (result.success) {
                showSuccessToast("A rendelés sikeresen mentve!");
                navigate("/TuzesSzine");
            } else {
                showErrorToast("Hiba történt a rendelés mentésekor!");
            }
        }
    };

    const canProceed =
        selectedMode === "Többszínű" ||
        (selectedMode === "Egyszínű" && selectedColorName);

    if (loading) {
        return (
            <TablerLayout title="Színek kiválasztása">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Betöltés...</span>
                        </div>
                        <h5 className="text-muted">Adatok betöltése...</h5>
                    </div>
                </div>
            </TablerLayout>
        );
    }

    if (error) {
        return (
            <TablerLayout title="Színek kiválasztása">
                <div className="alert alert-danger text-center my-4">
                    <i className="ti ti-alert-triangle me-2"></i>
                    Hiba történt a színek betöltésekor: {error}
                </div>
            </TablerLayout>
        );
    }

    return (
        <TablerLayout title="Színek kiválasztása">
            <div className="row justify-content-center">
                <div className="col-md-10">
                    <div className="card-header text-center"></div>
                    <div className="card-body">
                        <p className="mb-4 fw-bold fs-5 text-center">
                            Válassza ki, hogy <span className="text-primary">egy</span> vagy <span className="text-primary">többszínű</span> cipőt szeretne!
                        </p>
                        <div className="d-flex flex-wrap gap-4 justify-content-center mb-5">
                            <button
                                type="button"
                                className={`btn w-100 py-4 px-3 shadow-sm rounded-4
                                    ${selectedMode === "Egyszínű" ? "btn-primary" : "btn-outline-primary "}`}
                                style={{
                                    fontSize: 22,
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    minHeight: 70,
                                    minWidth: 250,
                                }}
                                onClick={() => handleAnswer("Egyszínű")}
                            >
                                Egyszínű
                            </button>
                            <button
                                type="button"
                                className={`btn w-100 py-4 px-3 shadow-sm rounded-4
                                    ${selectedMode === "Többszínű" ? "btn-primary" : "btn-outline-primary "}`}
                                style={{
                                    fontSize: 22,
                                    fontWeight: 600,
                                    borderWidth: 2,
                                    minHeight: 70,
                                    minWidth: 250,
                                }}
                                onClick={() => handleAnswer("Többszínű")}
                            >
                                Többszínű
                            </button>
                        </div>

                        {showOptions && (
                            <div className="row justify-content-center">
                                <div className="col-md-12">
                                    <div
                                        className="p-3 h-100 d-flex flex-column"
                                        style={{
                                            border: "1px solid #e2e8f0",
                                            backgroundColor: "#f9fafb",
                                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                        }}
                                    >
                                        <div
                                            style={{
                                                marginBottom: "10px",
                                                padding: "10px",
                                                backgroundColor: "#ffffff",
                                                border: "1px solid #e5e7eb",
                                                fontSize: "14px",
                                                fontWeight: "500",
                                                color: "black",
                                                textAlign: "center",
                                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                            }}
                                        >
                                            {selectedColorName
                                                ? `Kiválasztott szín: ${selectedColorName}`
                                                : "Nincs kiválasztva szín."}
                                        </div>

                                        <div
                                            className="scrollable-div"
                                            style={{
                                                maxHeight: "300px",
                                                overflowY: "auto",
                                                border: "1px solid #e2e8f0",
                                                padding: "10px",
                                                backgroundColor: "#ffffff",
                                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                            }}
                                        >
                                            {shoe_colors.map((color, index) => (
                                                <div
                                                    key={`${color.id}_${index}`}
                                                    className={`color-option ${
                                                        selectedColorId === color.id ? "selected" : ""
                                                    }`}
                                                    style={{
                                                        padding: "10px",
                                                        marginBottom: "6px",
                                                        cursor: "pointer",
                                                        border: "1px solid #e2e8f0",
                                                        backgroundColor:
                                                            selectedColorId === color.id
                                                                ? "#81c94a"
                                                                : "#f8fafc",
                                                        color: "black",
                                                        fontWeight: selectedColorId === color.id ? "600" : "500",
                                                        boxShadow:
                                                            selectedColorId === color.id
                                                                ? "0 2px 4px rgba(0, 0, 0, 0.2)"
                                                                : "0 1px 2px rgba(0, 0, 0, 0.1)",
                                                    }}
                                                    onClick={() => handleColorSelect(color)}
                                                >
                                                    {color.color_text}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="card-footer d-flex justify-content-between">
                        <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
                        <StepperNavButton side="right" label="Tovább" onClick={handleNext} disabled={!canProceed} />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default EgySzinu;